// features/fundos/api.ts — typed fetchers for the /v2/fundos/* surface.
//
// Everything goes through `apiRequest` so the X-API-Key header and the
// VITE_API_URL base resolution stay in one place (AGENTS.md §7).
//
// ponytail: search and series are POSTs with a JSON body, not GETs with a
// query string. That's the v2 rule (AGENTS.md §1.5) — nginx URL-decodes
// `%2F` before proxying, which mangles a formatted CNPJ mid-path — so there
// is no `v2Path` call here for those two. `/detail` does use a path, with
// the CNPJ as the final splat segment.
import { apiRequest } from "@/lib/api-client";

/**
 * The reporting regimes. FIF files daily; FII monthly; FIDC monthly under its
 * own CVM root; FIP quarterly, four-monthly since 2024. The populations do
 * not overlap.
 */
export type FundoFonte = "diario" | "mensal" | "fidc" | "fip";

/**
 * Fund families offered as a filter, in the order the dropdown lists them —
 * biggest population first. Mirrors `FUND_TYPES` in the backend, which is
 * what validates the value; this is only the label copy.
 */
export const FUND_TYPES = ["FIF", "FII", "FIDC", "FIP", "FIAGRO"] as const;
export type FundType = (typeof FUND_TYPES)[number];

export const FUND_TYPE_LABELS: Record<FundType, string> = {
  FIF: "Financeiro (FIF)",
  FII: "Imobiliário (FII)",
  FIDC: "Direitos creditórios (FIDC)",
  FIP: "Participações (FIP)",
  FIAGRO: "Agroindustrial (FIAGRO)",
};

export interface FundoRow {
  cnpj: string;
  nome: string | null;
  /** CVM's raw wording, which differs between its two registries. */
  tipo: string | null;
  /**
   * `tipo` folded to one code per family — `FIF`, `FII`, `FIDC`, `FIP`,
   * `FIAGRO`, or a rarer one. This is the filter key; `tipo` is not, because
   * CVM writes the same family two different ways.
   */
  tipo_codigo: string | null;
  classificacao: string | null;
  classificacao_anbima: string | null;
  situacao: string | null;
  administrador: string | null;
  gestor: string | null;
  /** Competency date of the latest informe — ISO `YYYY-MM-DD`, null if never reported. */
  dt_comptc: string | null;
  vl_quota: number | null;
  vl_patrim_liq: number | null;
  nr_cotst: number | null;
  /** B3 trading code, for the listed funds that have one (FIIs, ETFs). */
  ticker: string | null;
  /** ISIN of the fund's quota, when CVM publishes one. */
  isin: string | null;
  /**
   * Which reporting regime the figures came from. Only `diario` is daily —
   * everything else is a month-end, quarter-end or four-month-end snapshot
   * with no captação/resgate, so branch on `!== "diario"`, never on one
   * regime's name.
   */
  fonte: FundoFonte | null;
  /**
   * Informe rows behind the figures. >1 means the fund files per subclass,
   * the values are sums, and `vl_quota` is null (no single quota exists).
   */
  nr_subclasses: number | null;
  /** FII only: segmento de atuação. FIIs carry no ANBIMA classification. */
  segmento: string | null;
  /** FII only: gestão ativa or passiva. */
  gestao: string | null;
  /** Only present on ranking rows: the value of the ranked metric. */
  metric_value?: number | null;
}

export interface FundoSeriesPoint {
  dt_comptc: string;
  vl_quota: number | null;
  vl_patrim_liq: number | null;
  vl_total: number | null;
  captc_dia: number | null;
  resg_dia: number | null;
  nr_cotst: number | null;
}

/** Per-asset-class rollup, computed server-side over the whole portfolio. */
export interface BlocoTotal {
  bloco: string;
  positions: number;
  value: number;
  /** Share of the fund's total portfolio, 0..1. */
  share: number;
}

export interface FundosEnvelope<T> {
  data: T[];
  meta: {
    year: number;
    source: string;
    count: number;
    generated_at: string;
    month?: string;
    truncated?: boolean;
    /** Holdings only: per-asset-class rollup over the WHOLE portfolio. */
    blocos?: BlocoTotal[];
    /** Holdings only: total position count, which may exceed `data.length`. */
    positions?: number;
    totalValue?: number;
    [key: string]: unknown;
  };
}

export const RANKING_METRICS = ["patrimonio", "cotistas", "captacao", "resgate"] as const;
export type RankingMetric = (typeof RANKING_METRICS)[number];

export const RANKING_METRIC_LABELS: Record<RankingMetric, string> = {
  patrimonio: "Patrimônio líquido",
  cotistas: "Nº de cotistas",
  captacao: "Captação do dia",
  resgate: "Resgate do dia",
};

export const SCREENER_WINDOWS = [3, 6, 12, 24, 36] as const;
export type ScreenerWindow = (typeof SCREENER_WINDOWS)[number];

export const SCREENER_SORTS = [
  "sharpe",
  "sortino",
  "retorno",
  "excesso",
  "volatilidade",
  "drawdown",
  "captacao",
] as const;
export type ScreenerSort = (typeof SCREENER_SORTS)[number];

export const SCREENER_SORT_LABELS: Record<ScreenerSort, string> = {
  sharpe: "Sharpe",
  sortino: "Sortino",
  retorno: "Retorno",
  excesso: "Excesso vs pares",
  volatilidade: "Menor volatilidade",
  drawdown: "Menor drawdown",
  captacao: "Captação líquida",
};

/** Ratios are fractions (0.12 = 12%), not percentages. */
export interface FundoScreenerRow {
  cnpj: string;
  nome: string | null;
  classificacao_anbima: string | null;
  gestor: string | null;
  dt_inicio: string;
  dt_fim: string;
  dias: number;
  retorno: number;
  retorno_anual: number;
  volatilidade: number | null;
  sharpe: number | null;
  sortino: number | null;
  max_drawdown: number;
  excesso_categoria: number | null;
  captacao_liq_pct: number | null;
  vl_patrim_liq: number | null;
  nr_cotst: number | null;
}

export interface FundoScreenerBody {
  meses: ScreenerWindow;
  ordem: ScreenerSort;
  anbima?: string;
  min_pl?: number;
  limit?: number;
}

export function fetchFundoScreener(
  body: FundoScreenerBody,
  signal?: AbortSignal,
): Promise<FundosEnvelope<FundoScreenerRow>> {
  return apiRequest<FundosEnvelope<FundoScreenerRow>>("/v2/fundos/screener", {
    method: "POST",
    body,
    signal,
  });
}

export function searchFundos(
  body: { q?: string; limit?: number; offset?: number; tipo?: FundType },
  signal?: AbortSignal,
): Promise<FundosEnvelope<FundoRow>> {
  return apiRequest<FundosEnvelope<FundoRow>>("/v2/fundos/search", {
    method: "POST",
    body,
    signal,
  });
}

export function fetchFundo(cnpj: string, signal?: AbortSignal): Promise<FundosEnvelope<FundoRow>> {
  return apiRequest<FundosEnvelope<FundoRow>>(`/v2/fundos/detail/${cnpj}`, { signal });
}

/** ponytail: same shape as `companies/api.ts::TICKER_RE`. B3 trading codes
 *  for listed funds (FIIs like `KNRI11`, FIIM/ETFs like `BOVA11`) follow
 *  the same convention as equities — 4 alphanumeric chars (at least one
 *  letter), 1–2 class digits, optional `F` (fractional) or `B` (Bovespa
 *  Mais) suffix. A 14-digit CNPJ, masked CNPJ, or anything 8+ characters
 *  fails the regex and falls through to the canonical path unchanged. */
export const FUND_TICKER_RE = /^(?=.*[A-Za-z])[A-Za-z0-9]{4}\d{1,2}[FBfb]?$/;

/** CNPJ behind a fund trading code, or `undefined` when no fund carries it.
 *
 *  ponytail: reuses the funds search endpoint rather than adding a
 *  ticker→CNPJ route — the search already matches `ticker` (the
 *  ISIN-derived or CDA-derived B3 code) and ranks an exact hit above
 *  every fuzzy tier. Funds without a ticker (every non-listed FIF/FIDC/
 *  FIP) come back undefined and the redirect no-ops, so the user lands
 *  on the canonical CNPJ path with a fund-not-found empty state. */
export async function cnpjForTicker(
  ticker: string,
  opts: { signal?: AbortSignal } = {},
): Promise<string | undefined> {
  const upper = ticker.toUpperCase();
  const env = await searchFundos({ q: upper, limit: 5 }, opts.signal);
  // The matched row's `ticker` field is the canonical B3 code; a fuzzy
  // hit (where `q` matched the name) doesn't necessarily carry the
  // ticker. Restrict to exact-case matches to avoid the "LIKE '%X%'"
  // case where "X" matched the name but the fund is a different one.
  return env.data.find((row) => row.ticker === upper)?.cnpj;
}

export function fetchFundoSeries(
  body: { cnpj: string; since?: string; until?: string },
  signal?: AbortSignal,
): Promise<FundosEnvelope<FundoSeriesPoint>> {
  return apiRequest<FundosEnvelope<FundoSeriesPoint>>("/v2/fundos/series", {
    method: "POST",
    body,
    signal,
  });
}

export function fetchFundoRanking(
  metric: RankingMetric,
  limit: number,
  tipo?: FundType,
  signal?: AbortSignal,
): Promise<FundosEnvelope<FundoRow>> {
  // The filter is an extra PATH segment, not a query string — this surface
  // takes none (AGENTS.md §1.5), and the unfiltered URL stays as it was.
  const base = `/v2/fundos/ranking/metric/${metric}/limit/${limit}`;
  return apiRequest<FundosEnvelope<FundoRow>>(tipo ? `${base}/tipo/${tipo}` : base, { signal });
}

// ---------------------------------------------------------------------------
// Portfolio holdings (CDA), fact sheet (LAMINA), investor profile (PERFIL)
// ---------------------------------------------------------------------------

export interface HoldingRow {
  /** Asset class the position came from — CDA splits a portfolio into eight. */
  bloco: string | null;
  tp_aplic: string | null;
  tp_ativo: string | null;
  /** B3 ticker, SELIC code, swap code — whichever the bloco carries. */
  ativo_codigo: string | null;
  ativo_nome: string | null;
  isin: string | null;
  cnpj_emissor: string | null;
  qt_pos_final: number | null;
  vl_merc_pos_final: number | null;
  vl_custo_pos_final: number | null;
  /** Share of the fund's total portfolio, 0..1. */
  pr_carteira: number | null;
}

export interface HolderRow {
  cnpj_fundo: string;
  nome: string | null;
  administrador: string | null;
  gestor: string | null;
  qt_pos_final: number | null;
  vl_merc_pos_final: number | null;
  /** Share of THAT fund's portfolio held in the queried asset, 0..1. */
  pr_carteira: number | null;
}

export interface FundoLaminaRow {
  dt_comptc: string | null;
  nm_fantasia: string | null;
  publico_alvo: string | null;
  objetivo: string | null;
  polit_invest: string | null;
  taxa_adm: number | null;
  taxa_adm_max: number | null;
  taxa_perfm: string | null;
  taxa_entr: string | null;
  taxa_saida: string | null;
  invest_inicial_min: number | null;
  invest_adic: number | null;
  resgate_min: number | null;
  qt_dia_conversao_cota_resgate: number | null;
  qt_dia_pagto_resgate: number | null;
  qt_dia_caren: number | null;
  indice_refer: string | null;
  pr_rentab_fundo_5ano: number | null;
  pr_variacao_indice_refer_5ano: number | null;
  classe_risco_admin: string | null;
  vl_patrim_liq: number | null;
}

export interface FundoReturnPoint {
  dt_comptc: string;
  /** `YYYY-MM` — a real calendar month, derived server-side. */
  mes_rentab: string | null;
  pr_rentab_mes: number | null;
  pr_variacao_indice_refer_mes: number | null;
  pr_perfm_indice_refer_mes: number | null;
  /** FII only: the month's dividend yield, %. No FIF equivalent exists. */
  pr_dividend_yield_mes?: number | null;
}

export interface InvestorSlice {
  categoria: string;
  nr_cotst: number | null;
  /** Share of the fund's PL held by this category, 0..100. */
  pr_pl: number | null;
}

export interface FundoProfileRow {
  dt_comptc: string | null;
  investidores: InvestorSlice[];
  /** Total cotistas — present even when the per-category breakdown isn't. */
  nr_cotst_total: number | null;
  pr_patrim_liq_maior_cotst: number | null;
  nr_dia_cinqu_perc: number | null;
  nr_dia_cem_perc: number | null;
  pr_ativo_cred_priv: number | null;
  pr_var_carteira: number | null;
  mod_var: string | null;
}

export function fetchFundoHoldings(
  body: { cnpj: string; month?: string; limit?: number },
  signal?: AbortSignal,
): Promise<FundosEnvelope<HoldingRow>> {
  return apiRequest<FundosEnvelope<HoldingRow>>("/v2/fundos/holdings", {
    method: "POST",
    body,
    signal,
  });
}

export function fetchTickerHolders(
  ticker: string,
  limit: number,
  signal?: AbortSignal,
): Promise<FundosEnvelope<HolderRow>> {
  return apiRequest<FundosEnvelope<HolderRow>>(
    `/v2/fundos/holders/ticker/${encodeURIComponent(ticker)}/limit/${limit}`,
    { signal },
  );
}

export function fetchFundoLamina(
  cnpj: string,
  signal?: AbortSignal,
): Promise<FundosEnvelope<FundoLaminaRow>> {
  return apiRequest<FundosEnvelope<FundoLaminaRow>>(`/v2/fundos/lamina/${cnpj}`, { signal });
}

export function fetchFundoReturns(
  body: { cnpj: string; since?: string },
  signal?: AbortSignal,
): Promise<FundosEnvelope<FundoReturnPoint>> {
  return apiRequest<FundosEnvelope<FundoReturnPoint>>("/v2/fundos/returns", {
    method: "POST",
    body,
    signal,
  });
}

export function fetchFundoProfile(
  cnpj: string,
  signal?: AbortSignal,
): Promise<FundosEnvelope<FundoProfileRow>> {
  return apiRequest<FundosEnvelope<FundoProfileRow>>(`/v2/fundos/profile/${cnpj}`, { signal });
}

// ---------------------------------------------------------------------------
// Dashboard KPIs
// ---------------------------------------------------------------------------

export interface FundoCategoria {
  categoria: string;
  fundos: number;
  patrimonio: number;
  cotistas: number;
  /** Share of total patrimônio, 0..1. */
  share: number;
}

// --- Documentos: accounting filings and the auditor's conclusion -----------

/**
 * One accounting filing. Unlike a company's DFP/ITR row this carries no
 * parsed line items — CVM publishes only the PDF and the auditor's verdict
 * for funds — so `link` is the payload.
 */
export interface FundoDocumento {
  dt_comptc: string | null;
  dt_receb: string | null;
  /** CVM's raw type code. */
  tp_doc: string | null;
  /** Human label for `tp_doc`. */
  tipo: string;
  /** The auditor's conclusion, verbatim. Null for unaudited filings. */
  parecer: string | null;
  link: string | null;
  fonte: "eventual" | "dfin";
}

export function fetchFundoDocumentos(
  cnpj: string,
  signal?: AbortSignal,
): Promise<FundosEnvelope<FundoDocumento>> {
  return apiRequest<FundosEnvelope<FundoDocumento>>(`/v2/fundos/documentos/${cnpj}`, { signal });
}

// --- Relatórios: the FII annual and quarterly filings ----------------------

/** One ownership band from the annual distribuição de cotistas. */
export interface CotistaBand {
  faixa: string;
  nr_cotst: number | null;
  qt_cotas: number | null;
  /** Percentages, 0..100 — the backend already scales CVM's fractions. */
  pr_cotas: number | null;
  pr_pf: number | null;
  pr_pj: number | null;
}

/** One line of the quarterly income statement, on both bases. */
export interface ResultadoLine {
  linha: string;
  contabil: number | null;
  financeiro: number | null;
}

export interface AtivoContabil {
  nome: string | null;
  valor: number | null;
  /**
   * Already a percent in CVM's file, unlike every other percentual in this
   * family. A handful of rows carry absurd values (one reads 1.4e10) — the
   * list is ordered by size so they never lead.
   */
  pr_valorizacao: number | null;
  pr_total: number | null;
}

export interface ImovelTrimestral {
  nome: string | null;
  endereco: string | null;
  area: number | null;
  pr_vacancia: number | null;
  pr_inadimplencia: number | null;
  pr_receitas: number | null;
}

/**
 * All four report blocks for one fund. Every array is empty for a fund that
 * files no FII report — which is every FIF, FIDC and FIP.
 */
export interface FundoRelatoriosRow {
  /** Reference date of the newest annual / quarterly filing read. */
  ano: string | null;
  trimestre: string | null;
  faixas: CotistaBand[];
  resultado: ResultadoLine[];
  ativos: AtivoContabil[];
  ativos_total: number;
  ativos_posicoes: number;
  imoveis: ImovelTrimestral[];
}

export function fetchFundoRelatorios(
  cnpj: string,
  signal?: AbortSignal,
): Promise<FundosEnvelope<FundoRelatoriosRow>> {
  return apiRequest<FundosEnvelope<FundoRelatoriosRow>>(`/v2/fundos/relatorios/${cnpj}`, {
    signal,
  });
}

export interface FundoStats {
  fundos: number;
  patrimonio: number;
  cotistas: number;
  captacao: number;
  resgate: number;
  /** Funds carrying a B3 ticker — FIIs and ETFs. */
  listados: number;
  /** Split by reporting regime — these four partition `fundos`. */
  fif: number;
  fii: number;
  fidc: number;
  fip: number;
  month: string | null;
  categorias: FundoCategoria[];
}

export function fetchFundoStats(signal?: AbortSignal): Promise<FundosEnvelope<FundoStats>> {
  return apiRequest<FundosEnvelope<FundoStats>>("/v2/fundos/stats", { signal });
}
